import { useState, useMemo } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle,
  Filter,
  CheckCircle,
  Trash2,
  Loader2,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import {
  useAlerts,
  useAlertCounts,
  useAcknowledgeAlert,
  useResolveAlert,
  useDismissAlert,
  useBulkAlertAction,
  type AlertType,
  type AlertStatus,
  type AlertFilters,
} from '../hooks/useAlerts';
import { AlertCard } from '../components/AlertCard';

const alertTypeOptions: { value: AlertType; label: string }[] = [
  { value: 'date_slip', label: 'Date Slip' },
  { value: 'stuck_task', label: 'Stuck Task' },
  { value: 'capacity_overload', label: 'Capacity Overload' },
  { value: 'skill_gap', label: 'Skill Gap' },
  { value: 'time_entry_conflict', label: 'Time Entry Conflict' },
  { value: 'pin_priority_conflict', label: 'Pin Conflict' },
  { value: 'explosion_review_required', label: 'BOM Review Required' },
  { value: 'scheduling_skipped', label: 'Scheduling Skipped' },
  { value: 'memory_pressure', label: 'Memory Pressure' },
  { value: 'partial_staged', label: 'Partial Staged' },
  { value: 'data_integrity', label: 'Data Issue' },
  { value: 'unconfirmed_order', label: 'Unconfirmed Order' },
];

const statusOptions: { value: AlertStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'resolved', label: 'Resolved' },
];

export function AlertsPage() {
  const [filters, setFilters] = useState<AlertFilters>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);

  const { data: alerts, isLoading, error, refetch } = useAlerts(filters);
  const { data: counts } = useAlertCounts();

  const acknowledgeMutation = useAcknowledgeAlert();
  const resolveMutation = useResolveAlert();
  const dismissMutation = useDismissAlert();
  const bulkMutation = useBulkAlertAction();

  const isAnyMutationPending =
    acknowledgeMutation.isPending ||
    resolveMutation.isPending ||
    dismissMutation.isPending ||
    bulkMutation.isPending;

  // Group alerts by severity/status for better organization
  const groupedAlerts = useMemo(() => {
    if (!alerts) return { active: [], acknowledged: [], resolved: [] };

    return {
      active: alerts.filter((a) => a.status === 'active'),
      acknowledged: alerts.filter((a) => a.status === 'acknowledged'),
      resolved: alerts.filter((a) => a.status === 'resolved'),
    };
  }, [alerts]);

  const handleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (!alerts) return;
    if (selectedIds.size === alerts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(alerts.map((a) => a.id)));
    }
  };

  const handleBulkAcknowledge = () => {
    if (selectedIds.size === 0) return;
    bulkMutation.mutate(
      { action: 'acknowledge', alertIds: Array.from(selectedIds) },
      { onSuccess: () => setSelectedIds(new Set()) }
    );
  };

  const handleBulkDismiss = () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Dismiss ${selectedIds.size} alert(s)?`)) return;
    bulkMutation.mutate(
      { action: 'dismiss', alertIds: Array.from(selectedIds) },
      { onSuccess: () => setSelectedIds(new Set()) }
    );
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Alerts</h1>
          <p className="text-sm text-foreground-muted mt-1">
            Monitor and resolve scheduling alerts
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isLoading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-border bg-background text-foreground hover:bg-background-subtle transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary cards */}
      {counts && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div
            className={`rounded-lg border p-4 ${
              counts.totalActive > 0
                ? 'border-danger bg-danger-subtle'
                : 'border-success bg-success-subtle'
            }`}
          >
            <div className="flex items-center gap-3">
              <AlertTriangle
                className={`h-6 w-6 ${counts.totalActive > 0 ? 'text-danger' : 'text-success'}`}
              />
              <div>
                <p className="text-2xl font-bold text-foreground">{counts.totalActive}</p>
                <p className="text-sm text-foreground-muted">Active Alerts</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-background p-4">
            <p className="text-2xl font-bold text-foreground">
              {counts.byStatus?.acknowledged || 0}
            </p>
            <p className="text-sm text-foreground-muted">Acknowledged</p>
          </div>

          <div className="rounded-lg border border-border bg-background p-4">
            <p className="text-2xl font-bold text-foreground">
              {counts.byStatus?.resolved || 0}
            </p>
            <p className="text-sm text-foreground-muted">Resolved</p>
          </div>

          <div className="rounded-lg border border-border bg-background p-4">
            <p className="text-2xl font-bold text-foreground">
              {(counts.byType?.date_slip || 0) +
                (counts.byType?.skill_gap || 0) +
                (counts.byType?.explosion_review_required || 0)}
            </p>
            <p className="text-sm text-foreground-muted">Critical</p>
          </div>
        </div>
      )}

      {/* Filters and bulk actions */}
      <div className="bg-background rounded-lg border border-border p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* Filter toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-border bg-background text-foreground hover:bg-background-subtle transition-colors"
          >
            <Filter className="h-4 w-4" />
            Filters
            {(filters.alertType || filters.status) && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary text-white rounded-full">
                {(filters.alertType ? 1 : 0) + (filters.status ? 1 : 0)}
              </span>
            )}
          </button>

          {/* Bulk actions */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-muted">
                {selectedIds.size} selected
              </span>
              <button
                onClick={handleBulkAcknowledge}
                disabled={isAnyMutationPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                <CheckCircle className="h-4 w-4" />
                Acknowledge
              </button>
              <button
                onClick={handleBulkDismiss}
                disabled={isAnyMutationPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-danger text-white hover:bg-danger/90 transition-colors disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                Dismiss
              </button>
            </div>
          )}
        </div>

        {/* Filter controls */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-border grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Alert Type
              </label>
              <select
                value={filters.alertType || ''}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    alertType: (e.target.value as AlertType) || undefined,
                  }))
                }
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">All Types</option>
                {alertTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Status
              </label>
              <select
                value={filters.status || ''}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    status: (e.target.value as AlertStatus) || undefined,
                  }))
                }
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">All Statuses</option>
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => setFilters({})}
                className="px-3 py-2 text-sm font-medium text-foreground-muted hover:text-foreground transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Alert list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-danger bg-danger-subtle p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-danger" />
            <div>
              <h3 className="font-medium text-danger-foreground">Failed to load alerts</h3>
              <p className="text-sm text-danger-foreground mt-1">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          </div>
        </div>
      ) : alerts && alerts.length > 0 ? (
        <div className="space-y-6">
          {/* Select all checkbox */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={selectedIds.size === alerts.length && alerts.length > 0}
              onChange={handleSelectAll}
              className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-sm text-foreground-muted">
              Select all ({alerts.length})
            </span>
          </div>

          {/* Active alerts section */}
          {groupedAlerts.active.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-danger animate-pulse" />
                Active ({groupedAlerts.active.length})
              </h2>
              <div className="space-y-3">
                {groupedAlerts.active.map((alert) => (
                  <AlertCard
                    key={alert.id}
                    alert={alert}
                    selected={selectedIds.has(alert.id)}
                    onSelect={handleSelect}
                    onAcknowledge={(id) => acknowledgeMutation.mutate(id)}
                    onResolve={(id) => resolveMutation.mutate({ id })}
                    onDismiss={(id) => {
                      if (window.confirm('Dismiss this alert?')) {
                        dismissMutation.mutate(id);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Acknowledged alerts section */}
          {groupedAlerts.acknowledged.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-3">
                Acknowledged ({groupedAlerts.acknowledged.length})
              </h2>
              <div className="space-y-3">
                {groupedAlerts.acknowledged.map((alert) => (
                  <AlertCard
                    key={alert.id}
                    alert={alert}
                    selected={selectedIds.has(alert.id)}
                    onSelect={handleSelect}
                    onResolve={(id) => resolveMutation.mutate({ id })}
                    onDismiss={(id) => {
                      if (window.confirm('Dismiss this alert?')) {
                        dismissMutation.mutate(id);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Resolved alerts section (collapsed by default) */}
          {groupedAlerts.resolved.length > 0 && !filters.status && (
            <details className="group">
              <summary className="cursor-pointer text-lg font-semibold text-foreground-muted mb-3 list-none flex items-center gap-2">
                <span className="group-open:rotate-90 transition-transform">▶</span>
                Resolved ({groupedAlerts.resolved.length})
              </summary>
              <div className="space-y-3 mt-3">
                {groupedAlerts.resolved.map((alert) => (
                  <AlertCard
                    key={alert.id}
                    alert={alert}
                    selected={selectedIds.has(alert.id)}
                    onSelect={handleSelect}
                    onDismiss={(id) => {
                      if (window.confirm('Delete this resolved alert?')) {
                        dismissMutation.mutate(id);
                      }
                    }}
                  />
                ))}
              </div>
            </details>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-border bg-background p-8 text-center">
          <CheckCircle className="h-8 w-8 text-success mx-auto mb-3" />
          <p className="text-foreground font-medium">No alerts found</p>
          <p className="text-sm text-foreground-muted mt-1">
            {filters.alertType || filters.status
              ? 'Try adjusting your filters'
              : 'All systems running smoothly'}
          </p>
        </div>
      )}
    </div>
  );
}
